import { MakePayout } from "@/routes/(app)/_main-layout/_balances/-components/payout-form";
import { EU } from "@/components/svg/eu";
import { Gbp } from "@/components/svg/gbp";
import { numberToDecimalWithCurrency, cn } from "@/lib/utils";
import { VolumeApi } from "@/lib/volume-api";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { AuthorityRequired } from "@/lib/auth";
import { Spinner } from "@/components/ui/spinner";
import { Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  formatDayToAbbreviation,
  ScheduleSettings,
  days as daysArray,
} from "@/components/schedule-settings";
import { PayinAccountsWithAggregatedBalancesResponse } from "@/types/payin-accounts-with-aggregated-balances";
import { useState } from "react";

export const Route = createFileRoute(
  "/(app)/_main-layout/_balances/balances/overview",
)({
  component: () => <BalancesComponent />,
  loader: ({ context }) => {
    const api = new VolumeApi(
      context.accessToken,
      context.selectedApplicationId,
    );
    return api.getPayinAccountsWithAggregateBalances({
      merchantId: context.merchantId,
    });
  },
  pendingComponent: () => (
    <div>
      Please wait. This may take up to 10 seconds.
      <Spinner width={40} height={40} />
    </div>
  ),
});

function balanceOrNotAvailableText(
  balance: number | null,
  currency: string,
): string {
  if (balance === null) return "Not available at this time";
  return numberToDecimalWithCurrency(balance, currency);
}

const BalancesComponent = () => {
  const payinAccounts = Route.useLoaderData();

  return (
    <>
      {payinAccounts.map((payinAccount, index) => {
        return (
          <div
            className="mb-6"
            key={`${payinAccount.availableBalance}-${payinAccount.balance}-${payinAccount.accounts.length}-${index}`}
          >
            <div className="flex lg:grid lg:grid-cols-12 w-full rounded-t-2xl bg-[#E8EAFF] px-6 py-3">
              <div className="lg:col-span-5 text-xl font-bold text-volume-text-purple content-center flex items-center gap-2">
                {payinAccount.currency === "EUR" ? <EU /> : <Gbp />}Summary
              </div>

              <div className="lg:col-span-2 lg:text-right">
                <div>Account Balance</div>
                <div className="lg:text-right font-bold text-[20px]">
                  {balanceOrNotAvailableText(
                    payinAccount.balance,
                    payinAccount.currency,
                  )}
                </div>
              </div>
              <div className="col-span-1" />
              <div className="lg:col-span-2 lg:text-right">
                <div>Available Balance</div>
                <div className="lg:text-right font-bold  text-[20px]">
                  {balanceOrNotAvailableText(
                    payinAccount.availableBalance,
                    payinAccount.currency,
                  )}
                </div>
              </div>
            </div>
            <div className={"bg-white px-6 rounded-b-2xl"}>
              {payinAccount.accounts.map((account, index) => (
                <AccountItem
                  key={account.id}
                  account={account}
                  index={index}
                  totalAccounts={payinAccount.accounts.length}
                />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

// New AccountItem component
const AccountItem = ({
  account,
  index,
  totalAccounts,
}: {
  account: PayinAccountsWithAggregatedBalancesResponse["accounts"][number];
  index: number;
  totalAccounts: number;
}) => {
  const context = useRouteContext({
    from: "/(app)/_main-layout/_balances/balances/overview",
  });
  const [payoutScheduleDialogOpen, setPayoutScheduleDialogOpen] =
    useState(false);

  const formatSchedule = (
    schedule: PayinAccountsWithAggregatedBalancesResponse["accounts"][number]["payoutSchedule"],
  ) => {
    if (!schedule) return null;

    const days = schedule.days
      ?.map((d) => formatDayToAbbreviation(d))
      .sort((a, b) => {
        const dayOrder = daysArray.map((day) => day.label);
        return dayOrder.indexOf(a || "") - dayOrder.indexOf(b || "");
      })
      .join(", ");

    return `${days} at ${schedule.hour}:00`;
  };

  return (
    <div
      key={account.id}
      className={cn(totalAccounts - 1 !== index && "border-b-2", "py-4")}
    >
      <div
        className="py-2 lg:grid grid-cols-12"
        data-testid={`account-${account.id}`}
      >
        <div className="w-1/2 items-center py-2 lg:col-span-5">
          <div className="mb-1 flex items-center gap-2 font-bold">
            {account.accountHolderName}
            {account.forCurrency === "EUR" ? <EU /> : <Gbp />}
          </div>
          <div className="text-sm text-volume-dark-grey">
            {account.accountIdentification2 ? (
              <div>
                <div>
                  Account Number: {account.accountIdentification1.number}
                </div>
                <div>Sort Code: {account.accountIdentification2.number}</div>
              </div>
            ) : (
              <div>IBAN: {account.accountIdentification1.number}</div>
            )}
          </div>
        </div>
        <div className="lg:col-span-2 lg:text-right content-center">
          <div>Account Balance</div>
          <div className="lg:text-right font-bold text-[20px]">
            {balanceOrNotAvailableText(
              account.virtualAccountInformation.balance,
              account.forCurrency,
            )}
          </div>
        </div>
        <div className="col-span-1" />
        <div className="lg:col-span-2 lg:text-right content-center">
          <div>Available Balance</div>
          <div className="lg:text-right font-bold text-[20px]">
            {balanceOrNotAvailableText(
              account.virtualAccountInformation.availableBalance,
              account.forCurrency,
            )}
          </div>
        </div>
        <div className="lg:col-span-1" />
        <div className="ml-auto flex items-center">
          {account.isVirtualAccount &&
          !account.isLimitedVirtualAccount &&
          account.virtualAccountInformation.availableBalance &&
          account.virtualAccountInformation.balance ? (
            <AuthorityRequired
              authoritiesRequired={["INITIATE_PAYOUTS"]}
              authAuthorities={context.authorities}
            >
              <MakePayout payInAccount={account} />
            </AuthorityRequired>
          ) : null}
        </div>
      </div>

      {/* Payout Schedule Section */}
      {scheduledPayoutsEnabledForMerchant({
        account,
      }) ? (
        <div className="mt-3 pt-3 border-t border-dashed border-V_Base30">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-volume-dark-grey" />
                <span className="text-sm font-medium">Payout Schedule</span>
              </div>
              <Dialog
                open={payoutScheduleDialogOpen}
                onOpenChange={setPayoutScheduleDialogOpen}
              >
                <DialogTrigger asChild>
                  {account.payoutSchedule ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 hover:bg-V_Main10 rounded-full"
                    >
                      <Badge
                        variant="outline"
                        className="font-normal border-volume-text-purple text-volume-text-purple"
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatSchedule(account.payoutSchedule)}
                      </Badge>
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 hover:bg-V_Main10 rounded-full"
                    >
                      <Badge
                        variant="outline"
                        className="font-normal border-volume-text-purple text-volume-text-purple"
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        Set up automatic payouts
                      </Badge>
                    </Button>
                  )}
                </DialogTrigger>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle>
                      {account.payoutSchedule
                        ? "Edit Payout Schedule"
                        : "Set up automatic payouts"}
                    </DialogTitle>
                  </DialogHeader>
                  <ScheduleSettings
                    account={account}
                    setDialogOpen={setPayoutScheduleDialogOpen}
                  />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const scheduledPayoutsEnabledForMerchant = ({
  account,
}: {
  account: PayinAccountsWithAggregatedBalancesResponse["accounts"][number];
}) => {
  if (!account.isVirtualAccount) return false;
  if (account.forCurrency == "EUR") return false;
  if (account.isLimitedVirtualAccount) return false;
  if (account.virtualAccountInformation.availableBalance === null) return false;
  if (account.virtualAccountInformation.balance === null) return false;
  return true;
};
